// QuickBooks Online — the rules for reading the books into CAERA.
//
// Runtime-neutral: the `qbo-sync` edge function imports this through
// `_shared/lib`, and the P&L page imports it directly, so there is one reading
// of a QuickBooks report and one answer to "what does this account count as".
//
// CAERA already knows revenue (claim_lines) and caregiver and office pay
// (payroll_runs — salaried office staff are paid through runs too, see
// OverheadExpenses.js). What it does not know is everything else the business
// spends, and that is what QuickBooks is for here. So every QuickBooks account
// gets one TREATMENT, and only `overhead` accounts flow into CAERA's P&L. The
// others are read for the books-vs-CAERA comparison and nothing else, so an
// account can never be counted in CAERA twice.

export const TREATMENTS = [
  { key: 'revenue',  label: 'Revenue',        desc: 'Money in. CAERA counts revenue from claim lines; shown for comparison only.' },
  { key: 'payroll',  label: 'Payroll',        desc: 'Caregiver and staff pay. CAERA counts this from payroll runs; shown for comparison only.' },
  { key: 'overhead', label: 'Overhead',       desc: 'Everything else the business spends. Flows into the CAERA P&L.' },
  { key: 'ignore',   label: 'Leave out',      desc: 'Not an operating cost (owner draws, transfers, one-offs).' },
];
export const TREATMENT_KEYS = TREATMENTS.map(t => t.key);

// QuickBooks report section groups, as the Reports API names them. Any other
// group (GrossProfit, NetIncome…) is a total and has no section.
export const sectionOf = (group) =>
  group === 'Income' ? 'income'
  : group === 'OtherIncome' ? 'other_income'
  : group === 'COGS' ? 'cogs'
  : group === 'Expenses' ? 'expense'
  : group === 'OtherExpenses' ? 'other_expense'
  : null;

export const isIncomeSection = (section) => section === 'income' || section === 'other_income';

/**
 * What an account counts as before anyone has said otherwise. Deliberately
 * conservative: when unsure it says overhead, which is visible on the P&L and
 * can be changed, rather than `ignore`, which hides money.
 *
 *   income sections                   → revenue
 *   anything named contractor         → payroll  (1099 caregivers, paid in runs)
 *   wages / salaries                  → payroll  (W-2 staff, paid in runs)
 *   everything else that is a cost    → overhead (incl. payroll taxes and fees,
 *                                                 which runs do not carry)
 */
export const defaultTreatment = ({ section, name = '', fullName = '' }) => {
  if (isIncomeSection(section)) return 'revenue';
  const n = `${fullName} ${name}`.toLowerCase();
  if (/contract(or|ed)\b|subcontract/.test(n)) return 'payroll';
  const leaf = name.toLowerCase().trim();
  if (/^(wages|salar(y|ies)|gross wages|officer wages|staff wages)$/.test(leaf)) return 'payroll';
  return 'overhead';
};

const toNumber = (v) => {
  if (v === undefined || v === null || v === '') return 0;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// A month column carries StartDate in its metadata; the Total column carries
// ColKey 'total' and is skipped so nothing is counted twice.
const monthColumns = (report) => {
  const cols = report?.Columns?.Column || [];
  const out = [];
  cols.forEach((c, i) => {
    const meta = Object.fromEntries((c.MetaData || []).map(m => [m.Name, m.Value]));
    if (c.ColType !== 'Money' || !meta.StartDate) return;
    if (String(meta.ColKey || '').toLowerCase() === 'total') return;
    out.push({ index: i, month: `${meta.StartDate.slice(0, 7)}-01` });
  });
  return out;
};

/**
 * Flatten a ProfitAndLoss report (summarize_column_by=Month) into leaf-account
 * amounts per month.
 *
 * Returns { accounts: [{ accountId, name, fullName, section }],
 *           amounts:  [{ accountId, month: 'YYYY-MM-01', amount }] }
 *
 * Only Data rows are read. Section summaries ("Total Payroll expenses", Gross
 * Profit, Net Income) are totals of the Data rows and would double-count.
 * A parent account with direct postings appears as its own Data row inside
 * its section, and is read like any other.
 */
export const parseProfitAndLoss = (report) => {
  const cols = monthColumns(report);
  const accounts = new Map();
  const amounts = [];

  const walk = (rows, section, path) => {
    for (const row of rows || []) {
      if (row.type === 'Section' || row.Rows) {
        const group = row.group;
        const sec = section || sectionOf(group);
        // Top-level summaries (GrossProfit, NetIncome…) have no section of
        // their own and no Data rows worth reading.
        if (!sec) continue;
        const header = row.Header?.ColData?.[0];
        const nextPath = header?.value && section ? [...path, header.value] : path;
        walk(row.Rows?.Row, sec, nextPath);
        continue;
      }
      if (row.type && row.type !== 'Data') continue;
      if (!section) continue;
      const cell = row.ColData?.[0] || {};
      const name = String(cell.value || '').trim();
      if (!name) continue;
      // An account with direct postings under a parent section repeats the
      // parent's name; keep its path as the parent alone.
      const parentPath = path[path.length - 1] === name ? path.slice(0, -1) : path;
      const fullName = [...parentPath, name].join(':');
      const accountId = cell.id ? String(cell.id) : `name:${fullName}`;
      if (!accounts.has(accountId)) accounts.set(accountId, { accountId, name, fullName, section });
      for (const c of cols) {
        const amount = toNumber(row.ColData?.[c.index]?.value);
        if (amount !== 0) amounts.push({ accountId, month: c.month, amount });
      }
    }
  };
  walk(report?.Rows?.Row, null, []);

  return { accounts: [...accounts.values()], amounts };
};

/**
 * Books totals by month from synced rows and the account list.
 * rows: [{ month, account_id, amount }]; accounts: [{ account_id, section, treatment }]
 * Returns { 'YYYY-MM': { revenue, payroll, overhead, ignored, income, expense, net } }.
 * `net` is income minus every cost, whatever its treatment — the books' own
 * bottom line, which is what QuickBooks' P&L shows.
 */
export const summariseBooks = (rows, accounts) => {
  const byId = new Map((accounts || []).map(a => [a.account_id, a]));
  const out = {};
  for (const r of rows || []) {
    const a = byId.get(r.account_id);
    if (!a) continue;
    const key = String(r.month).slice(0, 7);
    const m = out[key] || (out[key] = { revenue: 0, payroll: 0, overhead: 0, ignored: 0, income: 0, expense: 0, net: 0 });
    const amt = Number(r.amount) || 0;
    const t = a.treatment || 'overhead';
    if (t === 'ignore') m.ignored += amt; else m[t] += amt;
    if (isIncomeSection(a.section)) m.income += amt; else m.expense += amt;
  }
  for (const m of Object.values(out)) m.net = m.income - m.expense;
  return out;
};
